import { Edit, Trash2, ArrowRight } from "lucide-react";
import { MoreVertical } from "lucide-react";
import {
  useDeleteBomUserMutation,
  useGetBOMPositions,
  useGetBomAssociateUserByCommunityId,
  useGetBomUsersList,
  useGetPropertiesByCommunity,
  useUpdateBomUserMutation,
} from "@/services";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  Badge,
} from "@/components/ui";
import { editBomFormValues, editBomSchema } from "./validations";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  BOM_POSITION_ID,
  START_DATE,
  END_DATE,
  USER_IDS,
  COMMUNITY_ID,
  STATUS,
  BOM_USER_ID,
  USER_ID,
} from "./consts";
import { cn, getRequiredFields } from "@/lib/utils";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

const useBOMs = () => {
  const [isAdd, setIsAdd] = useState(false);
  const [open, setOpen] = useState(false);
  const [isView, setIsView] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deletedRow, setDeletedRow] = useState<any>(null);
  const [swapConfirmDialog, setSwapConfirmDialog] = useState(false);
  const [swapBOMDetails, setSwapBOMDetails] = useState(null);
  const queryClient = useQueryClient();

  const editFrom = useForm<editBomFormValues>({
    defaultValues: {
      [BOM_POSITION_ID]: "",
      [START_DATE]: "",
      [END_DATE]: "",
      [USER_IDS]: "",
      [COMMUNITY_ID]: "",
      [STATUS]: "",
      [BOM_USER_ID]: "",
      [USER_ID]: "",
    },
    resolver: zodResolver(editBomSchema),
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const communityId = communityDetail?.communityId as string;

  const { data: associateUsers } = useGetBomAssociateUserByCommunityId({
    communityId,
    queryParams: { enabled: !!communityId },
  });

  const { data: properties } = useGetPropertiesByCommunity({
    communityId,
    queryParams: { enabled: !!communityId },
  });

  const listLength = (payload: any) => {
    if (Array.isArray(payload)) return payload.length;
    if (Array.isArray(payload?.data)) return payload.data.length;
    return 0;
  };

  const isPropertyAndUserExists = useMemo(() => {
    return listLength(properties) > 0 && listLength(associateUsers) > 0;
  }, [properties, associateUsers]);

  // get all BOM users list
  const { data: BOMList, isLoading: isLoadingBom } = useGetBomUsersList({
    communityId: communityDetail?.communityId as string,
  });

  // to get all the positions for the community
  const { data: bomPositions, isLoading: isBOMPositionsLoading } =
    useGetBOMPositions({ communityId: communityDetail?.communityId as string });

  // Define Table Columns
  const columns = [
    {
      accessorKey: "userId",
      header: "User ID",
      accessorFn: (row: any) => String(row.userId),
    },
    {
      accessorKey: "firstName",
      header: "First Name",
      accessorFn: (row: any) => String(row?.firstName || ""),
    },
    {
      accessorKey: "lastName",
      header: "Last Name",
      accessorFn: (row: any) => String(row?.lastName || ""),
    },
    {
      accessorKey: "positionName",
      header: "BM Position",
      accessorFn: (row: any) => String(row?.positionName || ""),
    },
    {
      accessorKey: "startDate",
      header: "BM Start Date",
      accessorFn: (row: any) =>
        row?.startDate ? moment(row?.startDate).format("MM/DD/YYYY") : "",
    },
    {
      accessorKey: "endDate",
      header: "BM End Date",
      accessorFn: (row: any) =>
        row?.endDate ? moment(row?.endDate).format("MM/DD/YYYY") : "",
    },
    {
      accessorKey: "active",
      header: "Status",
      cell: (row: any) => {
        return (
          <Badge
            variant="outline"
            className={cn(
              row.row.original.active
                ? "border-green-600 bg-green-100 text-green-600"
                : "border-red-600 bg-red-100 text-red-600",
              "rounded-full px-4 py-2"
            )}
          >
            {row.row.original.active ? "Active" : "Inactive"}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row: any) => {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <MoreVertical className="w-5 h-7 cursor-pointer" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white border-0 shadow-md"
            >
              {row.row.original.active && (
                <DropdownMenuItem
                  className="flex items-center gap-2"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen(true);
                    editFrom.reset({
                      [BOM_POSITION_ID]:
                        row.row.original.bomPositionId?.toString(),
                      [START_DATE]: row.row.original.startDate || "",
                      [END_DATE]: row.row.original.endDate || "",
                      [STATUS]: row.row.original.active ? "active" : "inactive",
                      [BOM_USER_ID]: row.row.original.bomUserId?.toString(),
                      [USER_ID]: row.row.original.userId?.toString(),
                    });
                  }}
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDeletedRow(row.row.original);
                  setOpenDeleteDialog(true);
                }}
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </DropdownMenuItem>
              {row.row.original.active && (
                <DropdownMenuItem
                  className="flex items-center gap-2"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSwapConfirmDialog(true);
                    setSwapBOMDetails(row.row.original);
                  }}
                >
                  <ArrowRight className="w-4 h-4" />
                  Transfer
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const requiredEditBomFields = getRequiredFields(editBomSchema);

  const { mutate: updateBomUserInfo, isPending: isUpdateBomUserPending } =
    useUpdateBomUserMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("BM User Info Updated Successfully");
        editFrom.reset();
        queryClient.invalidateQueries({
          queryKey: ["bomUsersList", communityDetail?.communityId],
        });
        setOpen(false);
      },
    });

  const { mutate: deleteBomUser, isPending: isDeleteBomUserPending } =
    useDeleteBomUserMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("BM User Info Deleted Successfully");
        queryClient.invalidateQueries({
          queryKey: ["bomUsersList", communityDetail?.communityId],
        });
        setOpenDeleteDialog(false);
      },
    });

  const onAddBOM = () => {
    setIsAdd(true);
  };

  const onCancel = () => {
    setIsAdd(false);
  };

  const onSubmitUpdateBOM = (data: editBomFormValues) => {
    updateBomUserInfo({
      id: data[BOM_USER_ID] as string,
      data: {
        ...data,
        [COMMUNITY_ID]: communityDetail?.communityId,
        [END_DATE]:
          data.status?.toLowerCase() == "inactive"
            ? new Date()
            : data[END_DATE],
        active: data.status?.toLowerCase() == "active",
      },
    });
  };

  const onCloseDialog = () => {
    setOpen(false);
    setIsView(false);
  };

  const onDeleteConfirm = () => {
    deleteBomUser({ id: deletedRow?.bomUserId });
  };

  const onRowClick = (row: any) => {
    setOpen(true);
    editFrom.reset({
      [BOM_POSITION_ID]: row.original.bomPositionId?.toString(),
      [START_DATE]: row.original.startDate || "",
      [END_DATE]: row.original.endDate,
      [STATUS]: row.original.active ? "active" : "inactive",
      [BOM_USER_ID]: row.original.bomUserId?.toString(),
      [USER_ID]: row.original.userId?.toString(),
    });
    setIsView(true);
  };
  return {
    columns,
    data: BOMList,
    isLoading: isLoadingBom,
    onAddBOM,
    isAdd,
    onCancel,
    editFrom,
    onSubmitUpdateBOM,
    isUpdateBomUserPending,
    onCloseDialog,
    open,
    setOpen,
    requiredEditBomFields,
    isView,
    openDeleteDialog,
    setOpenDeleteDialog,
    onDeleteConfirm,
    isDeleteBomUserPending,
    swapConfirmDialog,
    setSwapConfirmDialog,
    swapBOMDetails,
    BOMList,
    bomPositions,
    isBOMPositionsLoading,
    onRowClick,
    deletedRow,
    isPropertyAndUserExists,
  };
};

export default useBOMs;
